<script setup>
/* global axios */
// Relatório de chamadas (wavoip): estatísticas + histórico filtrável com player
// de gravação. Registros criados quando uma chamada termina (cliente wavoip).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban/call_history`;

const stats = ref(null);
const calls = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = 20;
const loading = ref(true);
const filters = ref({ search: '', direction: '', status: '', from: '', to: '' });

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage)));

const STATUS_CLASS = {
  ENDED: 'bg-n-teal-3 text-n-teal-11',
  NOT_ANSWERED: 'bg-n-amber-3 text-n-amber-11',
  REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
  FAILED: 'bg-n-ruby-3 text-n-ruby-11',
};

const fmtDuration = s => {
  const sec = Number(s) || 0;
  const m = Math.floor(sec / 60);
  return `${m}:${String(sec % 60).padStart(2, '0')}`;
};
const fmtTotalDuration = s => {
  const sec = Number(s) || 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

const loadStats = async () => {
  try {
    stats.value = (
      await axios.get(`${base}/stats`, { params: filters.value })
    ).data.data;
  } catch (e) {
    stats.value = null;
  }
};
const loadCalls = async (p = 1) => {
  loading.value = true;
  page.value = p;
  try {
    const { data } = await axios.get(base, {
      params: { ...filters.value, page: p },
    });
    calls.value = data.data || [];
    total.value = data.total || 0;
  } catch (e) {
    calls.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};
const applyFilters = () => {
  loadStats();
  loadCalls(1);
};

onMounted(() => {
  loadStats();
  loadCalls(1);
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-6xl px-8 py-6 mx-auto">
      <h1 class="mb-1 text-heading-1 text-n-slate-12">{{ t('CALLS.title') }}</h1>
      <p class="mb-6 text-sm text-n-slate-11">{{ t('CALLS.subtitle') }}</p>

      <!-- stats -->
      <div
        v-if="stats"
        class="grid grid-cols-2 gap-4 mb-6 md:grid-cols-3 lg:grid-cols-5"
      >
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ stats.totalCalls }}
          </div>
          <div class="text-xs text-n-slate-11">{{ t('CALLS.stats.total') }}</div>
          <div class="text-xs text-n-slate-10">
            {{ stats.withRecording }} {{ t('CALLS.stats.withRecording') }}
          </div>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <div class="text-2xl font-semibold text-n-blue-11">
            {{ fmtTotalDuration(stats.totalDurationSeconds) }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ t('CALLS.stats.duration') }}
          </div>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <div class="text-2xl font-semibold text-n-blue-11">
            {{ stats.outgoing }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ t('CALLS.stats.outgoing') }}
          </div>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <div class="text-2xl font-semibold text-n-teal-11">
            {{ stats.incoming }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ t('CALLS.stats.incoming') }}
          </div>
        </div>
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <div class="text-2xl font-semibold text-n-slate-12">
            {{ stats.answerRate }}%
          </div>
          <div class="text-xs text-n-slate-11">
            {{ t('CALLS.stats.answerRate') }}
          </div>
          <div class="text-xs text-n-slate-10">
            {{ stats.missed }} {{ t('CALLS.stats.missed') }} ·
            {{ stats.rejected }} {{ t('CALLS.stats.rejected') }}
          </div>
        </div>
      </div>

      <!-- filtros -->
      <div class="flex flex-wrap gap-2 mb-4">
        <input
          v-model="filters.search"
          :placeholder="t('CALLS.filters.search')"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @keyup.enter="applyFilters"
        />
        <select
          v-model="filters.direction"
          class="pl-3 pr-9 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @change="applyFilters"
        >
          <option value="">{{ t('CALLS.filters.allDirections') }}</option>
          <option value="OUTGOING">{{ t('CALLS.direction.OUTGOING') }}</option>
          <option value="INCOMING">{{ t('CALLS.direction.INCOMING') }}</option>
        </select>
        <select
          v-model="filters.status"
          class="pl-3 pr-9 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @change="applyFilters"
        >
          <option value="">{{ t('CALLS.filters.allStatuses') }}</option>
          <option v-for="s in ['ENDED', 'NOT_ANSWERED', 'REJECTED', 'FAILED']" :key="s" :value="s">
            {{ t(`CALLS.status.${s}`) }}
          </option>
        </select>
        <input
          v-model="filters.from"
          type="date"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @change="applyFilters"
        />
        <input
          v-model="filters.to"
          type="date"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
          @change="applyFilters"
        />
      </div>

      <!-- tabela -->
      <div v-if="loading" class="py-12 text-sm text-center text-n-slate-10">…</div>
      <p
        v-else-if="!calls.length"
        class="py-12 text-sm text-center text-n-slate-10"
      >
        {{ t('CALLS.empty') }}
      </p>
      <div v-else class="border rounded-xl border-n-weak overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="px-3 py-2 text-left font-medium">{{ t('CALLS.cols.direction') }}</th>
              <th class="px-3 py-2 text-left font-medium">{{ t('CALLS.cols.contact') }}</th>
              <th class="px-3 py-2 text-left font-medium">{{ t('CALLS.cols.status') }}</th>
              <th class="px-3 py-2 text-left font-medium">{{ t('CALLS.cols.duration') }}</th>
              <th class="px-3 py-2 text-left font-medium">{{ t('CALLS.cols.date') }}</th>
              <th class="px-3 py-2 text-left font-medium">{{ t('CALLS.cols.recording') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="c in calls"
              :key="c.id"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="px-3 py-2">
                <span
                  class="i-lucide-phone-outgoing w-4 h-4 text-n-blue-11"
                  v-if="c.direction === 'OUTGOING'"
                />
                <span class="i-lucide-phone-incoming w-4 h-4 text-n-teal-11" v-else />
              </td>
              <td class="px-3 py-2">
                <div class="font-medium">
                  {{ c.contact_name || t('CALLS.unknown') }}
                </div>
                <div class="text-xs text-n-slate-10">{{ c.contact_phone }}</div>
              </td>
              <td class="px-3 py-2">
                <span
                  class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
                  :class="STATUS_CLASS[c.status] || 'bg-n-alpha-2 text-n-slate-11'"
                  >{{ t(`CALLS.status.${c.status}`, c.status) }}</span
                >
              </td>
              <td class="px-3 py-2">{{ fmtDuration(c.duration_seconds) }}</td>
              <td class="px-3 py-2 text-xs text-n-slate-10">
                {{ c.created_at ? new Date(c.created_at).toLocaleString() : '—' }}
              </td>
              <td class="px-3 py-2">
                <audio
                  v-if="c.recording_url"
                  :src="c.recording_url"
                  controls
                  class="h-8 max-w-[200px]"
                />
                <span v-else class="text-xs text-n-slate-9">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- paginação -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-center gap-3 mt-4 text-sm"
      >
        <button
          class="px-2 py-1 rounded border border-n-weak disabled:opacity-50"
          :disabled="page <= 1"
          @click="loadCalls(page - 1)"
        >
          ‹
        </button>
        <span class="text-n-slate-11">{{ page }} / {{ totalPages }}</span>
        <button
          class="px-2 py-1 rounded border border-n-weak disabled:opacity-50"
          :disabled="page >= totalPages"
          @click="loadCalls(page + 1)"
        >
          ›
        </button>
      </div>
    </div>
  </div>
</template>
